import React, { useEffect, useState } from 'react'
import { auth, db } from '../../../firebase'
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore'
import ImageUploader from '../../ImageUploader'
import FormInfoTip from '../../ui/FormInfoTip'
import MainEventerTimingFields from '../../roster/MainEventerTimingFields'
import { rosterFormInfo } from '../../../utils/rosterFormInfo'
import {
  MAIN_EVENTER_COOLDOWN_DAYS,
  MAIN_EVENTER_DURATION_DAYS,
  clampMainEventerTimingDays
} from '../../../utils/mainEventerCooldown'

export default function CreatePublicRosterForm ({ onBack, onComplete }) {
  const [rosterData, setRosterData] = useState({
    name: '',
    maxWrestlers: 6,
    maxSwap: 8,
    maxCredits: 300,
    dataInizioPunteggio: '',
    owner: '',
    imageUrl: '',
    description: '',
    gameType: 'Indipendente',
    minMaleWrestlers: 0,
    minFemaleWrestlers: 0,
    onlyMainRoster: false,
    onlyNXT: false,
    hasBackstage: false,
    backstageCount: 0,
    hasMainEventer: true,
    mainEventerDurationDays: MAIN_EVENTER_DURATION_DAYS,
    mainEventerCooldownDays: MAIN_EVENTER_COOLDOWN_DAYS
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)

  const MIN_WRESTLERS = 6
  const MAX_WRESTLERS = 20

  useEffect(() => {
    const user = auth.currentUser
    if (user) {
      setRosterData(prev => ({
        ...prev,
        owner: user.displayName || user.email.split('@')[0] || 'Giocatore'
      }))
    }
  }, [])

  useEffect(() => {
    setRosterData(prev => ({
      ...prev,
      maxCredits: prev.maxWrestlers * 50
    }))
  }, [rosterData.maxWrestlers])

  useEffect(() => {
    const backstageValid =
      !rosterData.hasBackstage ||
      (Number.isInteger(rosterData.backstageCount) &&
        rosterData.backstageCount > 0 &&
        rosterData.backstageCount <=
          Math.max(
            rosterData.maxWrestlers - (rosterData.hasMainEventer ? 1 : 0),
            0
          ))

    const isValid =
      rosterData.name.trim().length >= 3 &&
      rosterData.maxWrestlers >= MIN_WRESTLERS &&
      rosterData.maxWrestlers <= MAX_WRESTLERS &&
      rosterData.maxSwap >= 0 &&
      rosterData.maxCredits > 0 &&
      rosterData.dataInizioPunteggio !== '' &&
      rosterData.minMaleWrestlers + rosterData.minFemaleWrestlers <=
        rosterData.maxWrestlers &&
      rosterData.owner.trim().length > 0 &&
      backstageValid

    setIsFormValid(isValid)
  }, [rosterData])

  const handleInputChange = event => {
    const { name, value, type, checked } = event.target
    let newValue

    if (type === 'checkbox') {
      newValue = checked
    } else if (type === 'number' || type === 'range') {
      newValue = Number(value)
    } else {
      newValue = value
    }

    if (name === 'onlyMainRoster' && newValue === true) {
      setRosterData(prev => ({
        ...prev,
        onlyMainRoster: true,
        onlyNXT: false
      }))
      return
    }

    if (name === 'onlyNXT' && newValue === true) {
      setRosterData(prev => ({
        ...prev,
        onlyNXT: true,
        onlyMainRoster: false
      }))
      return
    }

    setRosterData(prev => ({
      ...prev,
      [name]: newValue
    }))

    if (error) setError('')
  }

  const getTodayDate = () => new Date().toISOString().split('T')[0]

  const handleSubmit = async event => {
    event.preventDefault()
    setError('')
    setSuccess('')
    setJoinCode('')

    if (!isFormValid) {
      setError('Compila correttamente tutti i campi obbligatori')
      return
    }

    if (
      rosterData.minMaleWrestlers + rosterData.minFemaleWrestlers >
      rosterData.maxWrestlers
    ) {
      setError(
        'La somma dei wrestler minimi non può superare il totale massimo'
      )
      return
    }

    setLoading(true)

    try {
      const user = auth.currentUser
      if (!user) throw new Error('Utente non autenticato')

      const dataInizioTimestamp = new Date(rosterData.dataInizioPunteggio)

      const rosterToSave = {
        name: rosterData.name,
        maxWrestlers: rosterData.maxWrestlers,
        maxSwap: rosterData.maxSwap,
        maxCredits: rosterData.maxCredits,
        participantIds: [user.uid],
        ownerUid: user.uid,
        imageUrl: rosterData.imageUrl,
        description: rosterData.description,
        gameType: rosterData.gameType,
        dataInizioPunteggio: dataInizioTimestamp,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        minMaleWrestlers: rosterData.minMaleWrestlers,
        minFemaleWrestlers: rosterData.minFemaleWrestlers,
        onlyMainRoster: rosterData.onlyMainRoster,
        onlyNXT: rosterData.onlyNXT,
        hasBackstage: rosterData.hasBackstage,
        backstageCount: rosterData.hasBackstage ? rosterData.backstageCount : 0,
        hasMainEventer: rosterData.hasMainEventer,
        mainEventerDurationDays: clampMainEventerTimingDays(
          rosterData.mainEventerDurationDays,
          MAIN_EVENTER_DURATION_DAYS
        ),
        mainEventerCooldownDays: clampMainEventerTimingDays(
          rosterData.mainEventerCooldownDays,
          MAIN_EVENTER_COOLDOWN_DAYS
        ),
        private: true
      }

      const docRef = await addDoc(collection(db, 'rosters'), rosterToSave)
      const generatedCode = `${window.location.origin}/invite/${docRef.id}`

      await updateDoc(doc(db, 'users', user.uid), {
        ownerOfRosters: arrayUnion(docRef.id),
        rosterIds: arrayUnion(docRef.id)
      })

      setJoinCode(generatedCode)
      setSuccess(`Roster "${rosterData.name}" creato con successo!`)
    } catch (submitError) {
      console.error('Errore nella creazione del roster:', submitError)
      setError('Errore nella creazione del roster. Riprova più tardi.')
    } finally {
      setLoading(false)
    }
  }

  const copyJoinCode = async () => {
    if (!joinCode) return
    try {
      await navigator.clipboard.writeText(joinCode)
      alert('Codice copiato! Condividilo con i giocatori per unirsi.')
    } catch (copyError) {
      console.error('Errore nella copia:', copyError)
      alert('Impossibile copiare il codice')
    }
  }

  return (
    <div className='bg-brand-dark'>
      <div className='container'>
        <div className='row justify-content-center'>
          <div className='col-12 col-lg-8'>
            <div>
              {success && !joinCode && (
                <div
                  className='alert alert-success d-flex align-items-center gap-2 mb-4 border-0 rounded-3'
                  style={{
                    background: 'rgba(46,204,113,0.1)',
                    color: '#2ecc71'
                  }}
                >
                  <i className='bi bi-check-circle-fill fs-5'></i>
                  <span>{success}</span>
                  <button
                    type='button'
                    className='btn-close ms-auto'
                    onClick={() => setSuccess('')}
                  ></button>
                </div>
              )}

              {error && (
                <div
                  className='alert alert-danger d-flex align-items-center gap-2 mb-4 border-0 rounded-3'
                  style={{
                    background: 'rgba(231,76,60,0.1)',
                    color: '#e74c3c'
                  }}
                >
                  <i className='bi bi-exclamation-triangle-fill fs-5'></i>
                  <span>{error}</span>
                  <button
                    type='button'
                    className='btn-close ms-auto'
                    onClick={() => setError('')}
                  ></button>
                </div>
              )}

              {joinCode ? (
                <div className='text-center py-3'>
                  <div className='mb-4'>
                    <div
                      className='rounded-circle d-inline-flex align-items-center justify-content-center p-3 mb-3'
                      style={{ background: 'rgba(46,204,113,0.15)' }}
                    >
                      <i
                        className='bi bi-check-lg fs-1'
                        style={{ color: '#2ecc71' }}
                      ></i>
                    </div>
                    <h5
                      className='fw-bold mb-2'
                      style={{ color: 'var(--brand-secondary)' }}
                    >
                      Roster creato con successo!
                    </h5>
                  </div>

                  <div
                    className='card bg-transparent border-0 rounded-4 p-3 p-sm-4 mb-4'
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(52,152,219,0.05) 0%, rgba(52,152,219,0.01) 100%)',
                      border: '1px solid rgba(52,152,219,0.2)'
                    }}
                  >
                    <label
                      className='fw-semibold mb-2 text-start'
                      style={{
                        color: 'var(--brand-secondary)',
                        fontSize: '0.9rem'
                      }}
                    >
                      <i className='bi bi-key-fill me-1'></i> Link di invito
                    </label>

                    <div className='d-flex flex-column flex-sm-row gap-2'>
                      <code
                        className='flex-grow-1 px-3 py-2 rounded-3 bg-dark text-light text-start'
                        style={{
                          fontSize: '0.85rem',
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                          whiteSpace: 'normal'
                        }}
                      >
                        {joinCode}
                      </code>
                      <button
                        className='btn px-3 py-2 d-flex align-items-center justify-content-center gap-2 flex-shrink-0'
                        onClick={copyJoinCode}
                        style={{
                          background: 'var(--brand-secondary)',
                          color: 'white'
                        }}
                        type='button'
                      >
                        <i className='bi bi-clipboard-fill'></i>
                        <span className='d-none d-sm-inline'>Copia</span>
                      </button>
                    </div>

                    <p className='text-brand-muted small mt-3 mb-0 text-start'>
                      Condividi questo link con i giocatori che vuoi invitare.
                    </p>
                  </div>

                  <button
                    type='button'
                    className='btn btn-brand-secondary roster-form-btn roster-form-btn--primary mt-2'
                    onClick={onComplete}
                  >
                    <i className='bi bi-check-lg me-2'></i> Completa
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>

                  <div className='mb-4'>
                    <div className='form-info-row--wrap mb-2'>
                      <label className='form-label fw-semibold mb-0 d-flex align-items-center gap-2 text-brand-secondary'>
                        <i className='bi bi-tag'></i>
                        <span className='text-light'>Nome Roster</span>
                        <span className='text-danger small'>*</span>
                      </label>

                    </div>
                    <input
                      type='text'
                      className='form-control py-2'
                      name='name'
                      value={rosterData.name}
                      onChange={handleInputChange}
                      maxLength='30'
                      placeholder='Es: Ring of Champions'
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white'
                      }}
                      required
                    />
                  </div>

                  <div className='bg-dark rounded-3 p-3 mb-4'>
                    <div className='form-info-row--between mb-3 text-brand-secondary'>
                      <div className='d-flex align-items-center gap-2'>
                        <i className='bi bi-sliders2'></i>
                        <span className='fw-semibold text-light'>
                          Wrestler e crediti
                        </span>
                      </div>
                      <FormInfoTip title={rosterFormInfo.slotsAndCredits.title}>
                        {rosterFormInfo.slotsAndCredits.body}
                      </FormInfoTip>
                    </div>

                    <div className='row g-3'>
                      <div className='col-6 text-brand-secondary'>
                        <label className='form-label small text-brand-muted mb-1'>
                          Wrestler per fazione
                        </label>
                        <div className='d-flex align-items-center gap-2'>
                          <i className='bi bi-people-fill'></i>
                          <input
                            type='range'
                            className='form-range flex-grow-1'
                            name='maxWrestlers'
                            min={MIN_WRESTLERS}
                            max={MAX_WRESTLERS}
                            step={1}
                            value={rosterData.maxWrestlers}
                            onChange={handleInputChange}
                          />
                          <span className='badge bg-brand-secondary text-dark rounded-pill'>
                            {rosterData.maxWrestlers}
                          </span>
                        </div>
                      </div>
                      <div className='col-6'>
                        <label className='form-label small text-brand-muted mb-1'>
                          Scambi max
                        </label>
                        <div className='d-flex align-items-center gap-2'>
                          <i className='bi bi-arrow-left-right text-brand-secondary'></i>
                          <input
                            type='number'
                            className='form-control form-control-sm'
                            name='maxSwap'
                            min={0}
                            value={rosterData.maxSwap}
                            onChange={handleInputChange}
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: 'none',
                              color: 'white'
                            }}
                          />
                        </div>
                      </div>
                      <div className='col-6'>
                        <label className='form-label small text-brand-muted mb-1'>
                          Crediti
                        </label>
                        <div className='d-flex align-items-center gap-2'>
                          <i className='bi bi-currency-dollar text-brand-secondary'></i>
                          <input
                            type='number'
                            className='form-control form-control-sm'
                            name='maxCredits'
                            value={rosterData.maxCredits}
                            onChange={handleInputChange}
                            step='10'
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: 'none',
                              color: 'white'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='form-info-row mb-2'>
                    <span className='small fw-semibold text-brand-secondary'>
                      Vincoli di composizione
                    </span>
                    <FormInfoTip title={rosterFormInfo.composition.title}>
                      {rosterFormInfo.composition.body}
                    </FormInfoTip>
                  </div>
                  <div className='row g-3 mb-4'>
                    <div className='col-6'>
                      <label className='form-label small text-brand-muted mb-1'>
                        Min. Uomini
                      </label>
                      <div className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-gender-male'
                          style={{ color: '#3498db' }}
                        ></i>
                        <input
                          type='range'
                          className='form-range flex-grow-1'
                          name='minMaleWrestlers'
                          min={0}
                          max={Math.floor(rosterData.maxWrestlers / 2)}
                          step={1}
                          value={rosterData.minMaleWrestlers}
                          onChange={handleInputChange}
                        />
                        <span className='badge bg-brand-secondary text-dark rounded-pill'>
                          {rosterData.minMaleWrestlers}
                        </span>
                      </div>
                    </div>
                    <div className='col-6'>
                      <label className='form-label small text-brand-muted mb-1'>
                        Min. Donne
                      </label>
                      <div className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-gender-female'
                          style={{ color: '#e84393' }}
                        ></i>
                        <input
                          type='range'
                          className='form-range flex-grow-1'
                          name='minFemaleWrestlers'
                          min={0}
                          max={Math.floor(rosterData.maxWrestlers / 2)}
                          step={1}
                          value={rosterData.minFemaleWrestlers}
                          onChange={handleInputChange}
                        />
                        <span className='badge bg-brand-secondary text-dark rounded-pill'>
                          {rosterData.minFemaleWrestlers}
                        </span>
                      </div>
                    </div>
                  </div>

                  {rosterData.minMaleWrestlers + rosterData.minFemaleWrestlers >
                    rosterData.maxWrestlers && (
                    <div
                      className='alert alert-warning py-2 mb-4 small'
                      style={{
                        background: 'rgba(243,156,18,0.1)',
                        border: 'none',
                        color: '#f39c12'
                      }}
                    >
                      <i className='bi bi-exclamation-triangle me-1'></i>
                      Somma minimi supera il max wrestler
                    </div>
                  )}

                  <div className='bg-dark rounded-3 p-3 mb-4'>
                    <div className='form-info-row--between mb-3'>
                      <span className='small fw-semibold text-light'>
                        Filtri e ruoli speciali
                      </span>
                      <FormInfoTip title={rosterFormInfo.options.title}>
                        {rosterFormInfo.options.body}
                      </FormInfoTip>
                    </div>
                    <div className='row g-2'>
                      <div className='col-6'>
                        <div className='d-flex align-items-center justify-content-between'>
                          <label
                            className='form-check-label small text-light'
                            htmlFor='onlyMainRosterPublic'
                          >
                            Main Roster
                          </label>
                          <div className='form-check form-switch m-0'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              role='switch'
                              name='onlyMainRoster'
                              id='onlyMainRosterPublic'
                              checked={rosterData.onlyMainRoster}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='col-6'>
                        <div className='d-flex align-items-center justify-content-between'>
                          <label
                            className='form-check-label small text-light'
                            htmlFor='onlyNXTPublic'
                          >
                            Solo NXT
                          </label>
                          <div className='form-check form-switch m-0'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              role='switch'
                              name='onlyNXT'
                              id='onlyNXTPublic'
                              checked={rosterData.onlyNXT}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='col-6'>
                        <div className='d-flex align-items-center justify-content-between'>
                          <label
                            className='form-check-label small text-light'
                            htmlFor='hasBackstagePublic'
                          >
                            Backstage
                          </label>
                          <div className='form-check form-switch m-0'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              role='switch'
                              name='hasBackstage'
                              id='hasBackstagePublic'
                              checked={rosterData.hasBackstage}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='col-6'>
                        <div className='d-flex align-items-center justify-content-between'>
                          <label
                            className='form-check-label small text-light'
                            htmlFor='hasMainEventerPublic'
                          >
                            Main Eventer
                          </label>
                          <div className='form-check form-switch m-0'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              role='switch'
                              name='hasMainEventer'
                              id='hasMainEventerPublic'
                              checked={rosterData.hasMainEventer}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <MainEventerTimingFields
                    enabled={rosterData.hasMainEventer}
                    durationDays={rosterData.mainEventerDurationDays}
                    cooldownDays={rosterData.mainEventerCooldownDays}
                    onDurationChange={value =>
                      setRosterData(prev => ({
                        ...prev,
                        mainEventerDurationDays: value
                      }))
                    }
                    onCooldownChange={value =>
                      setRosterData(prev => ({
                        ...prev,
                        mainEventerCooldownDays: value
                      }))
                    }
                    idPrefix='create-public-roster-main-eventer'
                  />

                  {rosterData.hasBackstage && (
                    <div className='mb-4'>
                      <div className='form-info-row mb-1'>
                        <label className='form-label small text-brand-muted mb-0'>
                          Wrestler in Backstage
                        </label>
                        <FormInfoTip title={rosterFormInfo.backstageCount.title}>
                          {rosterFormInfo.backstageCount.body}
                        </FormInfoTip>
                      </div>
                      <div className='d-flex align-items-center gap-3'>
                        <i className='bi bi-door-closed text-brand-secondary'></i>
                        <input
                          type='range'
                          className='form-range flex-grow-1'
                          name='backstageCount'
                          min={1}
                          max={Math.max(
                            rosterData.maxWrestlers -
                              (rosterData.hasMainEventer ? 1 : 0),
                            1
                          )}
                          step={1}
                          value={rosterData.backstageCount}
                          onChange={handleInputChange}
                        />
                        <span className='badge bg-brand-secondary text-dark rounded-pill'>
                          {rosterData.backstageCount}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className='mb-4'>
                    <div className='form-info-row--wrap mb-2'>
                      <label className='form-label fw-semibold mb-0 d-flex align-items-center gap-2 text-brand-secondary'>
                        <i className='bi bi-calendar-event'></i>
                        <span className='text-light'>Data inizio punteggio</span>
                        <span className='text-danger small'>*</span>
                      </label>
                      <FormInfoTip title={rosterFormInfo.startDate.title}>
                        {rosterFormInfo.startDate.body}
                      </FormInfoTip>
                    </div>
                    <input
                      type='date'
                      className='form-control py-2'
                      name='dataInizioPunteggio'
                      min={getTodayDate()}
                      value={rosterData.dataInizioPunteggio}
                      onChange={handleInputChange}
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white'
                      }}
                      required
                    />
                  </div>

                  <div className='mb-4'>
                    <label className='form-label small text-brand-muted mb-1'>
                      Descrizione (opzionale)
                    </label>
                    <textarea
                      className='form-control'
                      rows='2'
                      name='description'
                      value={rosterData.description}
                      onChange={handleInputChange}
                      placeholder='Descrivi il tuo roster...'
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white',
                        fontSize: '0.85rem'
                      }}
                    />
                  </div>

                  <div className='bg-dark rounded-3 p-3 mb-4'>
                    <div className='form-info-row--between mb-3 text-brand-secondary'>
                      <div className='d-flex align-items-center gap-2'>
                        <i className='bi bi-image'></i>
                        <span className='fw-semibold text-light'>
                          Immagine roster
                        </span>
                      </div>
                      <FormInfoTip title={rosterFormInfo.image.title}>
                        {rosterFormInfo.image.body}
                      </FormInfoTip>
                    </div>
                    <ImageUploader
                      currentImageUrl={rosterData.imageUrl}
                      onUploadSuccess={url =>
                        setRosterData(prev => ({ ...prev, imageUrl: url }))
                      }
                      storagePath='rosters'
                      buttonText={
                        rosterData.imageUrl
                          ? 'Cambia immagine'
                          : 'Carica immagine'
                      }
                    />
                    {rosterData.imageUrl && (
                      <button
                        type='button'
                        className='btn btn-outline-danger btn-sm'
                        onClick={() =>
                          setRosterData(prev => ({ ...prev, imageUrl: '' }))
                        }
                      >
                        <i className='bi bi-x-circle me-1'></i>
                        Rimuovi immagine
                      </button>
                    )}
                  </div>

                  <button
                    type='submit'
                    className='btn btn-brand-secondary roster-form-btn roster-form-btn--primary roster-form-btn--block'
                    disabled={!isFormValid || loading}
                  >
                    {loading ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2'></span>
                        Creazione in corso...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-check-circle me-2'></i>
                        Crea Roster
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
